/**
 * Configuration for hardcoded question overrides
 *
 * Keys: partner_questionnaire_question_id
 * Values: Object with optional title and description overrides per language
 */

interface QuestionOverride {
  title?: string;
  description?: string;
  placeholder?: string;
}

interface QuestionOverrides {
  en?: QuestionOverride;
  es?: QuestionOverride;
}

export const QUESTION_OVERRIDES: Record<string, QuestionOverrides> = {
  // English: Lab Results file upload
  "6b989660-5e64-441d-97d1-43d100a16c3f": {
    en: {
      title: "Lab Results",
      description: "Your doctor may require you to upload your labs prior to prescribing medication.",
    },
  },

  // English: Lab test details text input
  "3fd3203b-6315-42c3-a7c0-25b26c5756cd": {
    en: {
      description: "<ul><li>We need information about your Creatinine, GFR, or CrCl.</li></ul>",
    },
  },

  // Spanish: Lab Results file upload
  "a9cf49cd-c132-4990-bc11-8137721d0a06": {
    es: {
      title: "Resultados de Laboratorio",
      description: "Su médico puede requerir que suba sus análisis antes de prescribir medicación.",
    },
  },

  // Spanish descriptions (existing)
  "a7b5d146-da23-47a1-a1ed-e3c5b1b49c42": {
    es: {
      description: `<div><span style="background-color: transparent;">Escribe los detalles aquí</span></div>`,
    },
  },

  "6cf1c73e-a0fc-423a-b56a-96c64480a5f7": {
    es: {
      description: `<div>La hipertensión pulmonar NO es lo mismo que la hipertensión esencial.<br data-start="249" data-end="252">`,
    },
  },

  "0908f0a5-fb33-4c05-af05-8667f85601f5": {
    es: {
      description: `<div><ul><li><span style="background-color: transparent;">Si no pudo subir una foto/archivo de sus análisis, por favor escríbalos aquí.</span></li></ul></div>`,
    },
  },

  "e8969ee7-70c6-4cc9-9bd7-ab913dd3d799": {
    es: {
      description: `Por favor describa los motivos de su consulta con el especialista renal/de riñones.`,
    },
  },

  "0875937a-b330-4845-80d8-caa795249014": {
    es: {
      description: `<div>Por favor, tenga en cuenta: el uso de medicamentos para la disfunción eréctil junto con ciertas drogas recreativas puede resultar en la muerte.</div>`,
    },
  },

  "3157ba74-dfa3-4ad8-a582-6876748ca5e6": {
    es: {
      description: `<div>Nota: El uso de medicamentos para la disfunción eréctil en combinación con nitratos (a menudo recetados para el dolor de pecho o la angina) u otros fármacos puede ser mortal. Por favor, asegúrese de seleccionar la o las opciones que correspondan a su caso.</div>`,
    },
  },

  "1b29d73d-b3ce-4f70-82b3-57b6f9a7eab3": {
    es: {
      description: `<div>Esto incluye peso, presión arterial y frecuencia cardíaca.</div>`,
    },
  },

  "afcd2c8b-d802-4a0a-8e4d-24c952c4510d": {
    en: {
      title: "This is your first message to the doctor. Please introduce yourself and, if you wish:",
      description: `<div><ul><li>Ask any questions you may have.</li>
<li>Mention any other medical conditions that were not included in the questions.</li>
<li>Include any additional information you believe your doctor should know.</li>
<li>For example: asthma, high blood pressure, among others.</li></ul></div>`,
      placeholder: 'Example: "Hi doctor, other than what I already answered I am otherwise healthy and excited to get started."',
    },
    es: {
      title: "Este es tu primer mensaje para el médico. Por favor, preséntese y si deseas:",
      description: `<div><ul><li>Puedes realizar cualquier pregunta.</li>
<li>Mencionar otros problemas médicos que no hayan sido mencionados en las preguntas.</li>
<li>Incluir cualquier información adicional que creas que tu médico deba conocer.</li>
<li>Ej. Asma, presión alta, entre otros.</li></ul></div>`,
      placeholder: 'Ejemplo: "Hola doctor/a, aparte de lo que ya respondí, estoy saludable y con ganas de comenzar."',
    },
  },

  "6aa8de78-5af0-480a-938a-0d48cd6cfb65": {
    es: {
      description: `<p class="p1" style="font-variant-numeric: normal; font-variant-east-asian: normal; font-variant-alternates: normal; font-variant-position: normal; font-variant-emoji: normal; font-size-adjust: none; font-kerning: auto; font-optical-sizing: auto; font-feature-settings: normal; font-variation-settings: normal; font-stretch: normal; line-height: normal;"><ul><li>La FDA NO ha asignado una categoría específica de embarazo para PT-141 (Bremelanotida), pero la etiqueta desaconseja su uso. Se recomienda a personas con potencial reproductivo que usen anticonceptivos y suspendan el uso si se confirma un embarazo.</li></ul><span style="font-size: 18px; white-space-collapse: collapse;"></span></p>`,
    },
  },

  "57351939-e553-45cf-b3e3-7a42f3f4da5b": {
    es: {
      description: `<div>Usted mencionó que está planeando un embarazo, está embarazada o está amamantando. Por la seguridad de su hijo o hija, no es candidato/a para PT-141 (Bremelanotida) en este momento. Por favor, envíe un correo electrónico a [customer_support_email] para opciones alternativas.<span></span></div>`,
    },
  },

  "65b18e2f-57c0-4413-ba3d-6b1884a260d2": {
    es: {
      description: `<div><ol><li><strong data-start="74" data-end="134" style="font-size: 1rem; white-space-collapse: preserve-breaks; background-color: transparent;">Antecedentes de infarto de miocardio (ataque al corazón)</strong></li><li data-start="137" data-end="171"><p data-start="139" data-end="171"><strong data-start="139" data-end="169">Dolor en el pecho (angina)</strong></p></li><li data-start="172" data-end="244"><p data-start="174" data-end="244"><strong data-start="174" data-end="242">Accidente cerebrovascular o mini accidente cerebrovascular (AIT)</strong></p></li><li data-start="245" data-end="285"><p data-start="247" data-end="285"><strong data-start="247" data-end="283">Hipertensión grave no controlada</strong></p></li><li data-start="286" data-end="311"><p data-start="288" data-end="311"><strong data-start="288" data-end="309">Hipotensión grave</strong></p></li><li data-start="312" data-end="392"><p data-start="314" data-end="392"><strong data-start="314" data-end="390">Latidos irregulares del corazón (arritmias, fibrilación auricular, etc.)</strong></p></li><li data-start="393" data-end="421"><p data-start="395" data-end="421"><strong data-start="395" data-end="421">Insuficiencia cardíaca</strong></p></li></ol>

  </div>`,
    },
  },

  "8310421f-ae56-4eef-b8f5-563b5a8d841c": {
    en: {
      title: "This is your first message to the doctor. Please introduce yourself and, if you wish:",
      description: `<div><ul><li>Ask any questions you may have.</li>
<li>Mention any other medical conditions that were not included in the questions.</li>
<li>Include any additional information you believe your doctor should know.</li>
<li>For example: asthma, high blood pressure, among others.</li></ul></div>`,
      placeholder: 'Example: "Hi doctor, other than what I already answered I am otherwise healthy and excited to get started."',
    },
  },

  "4331230b-a18a-4164-9a2f-fcb271b3d4fd": {
    en: {
      title: "This is your first message to the doctor. Please introduce yourself and, if you wish:",
      description: `<div><ul><li>Ask any questions you may have.</li>
<li>Mention any other medical conditions that were not included in the questions.</li>
<li>Include any additional information you believe your doctor should know.</li>
<li>For example: asthma, high blood pressure, among others.</li></ul></div>`,
      placeholder: 'Example: "Hi doctor, other than what I already answered I am otherwise healthy and excited to get started."',
    },
  },

  "2f02cfe6-8365-4c7c-9a62-0958759924c6": {
    es: {
      description: `<div><p data-start="71" data-end="357"><b><span data-start="71" data-end="108">Acerca de PT-141 (Bremelanotida):</span><br data-start="108" data-end="111"></b>
  PT-141 es un agonista de los receptores de melanocortina que puede aumentar el deseo y la excitación sexual. Está aprobado por la FDA para el trastorno de deseo sexual hipoactivo en mujeres y se usa fuera de indicación en hombres con bajo libido.</p>
  <p data-start="359" data-end="377"><span data-start="359" data-end="375"><b>Cómo usarlo:</b></span></p>
  <ul data-start="378" data-end="665">
  <li data-start="378" data-end="412">
  <p data-start="380" data-end="412">No use más de <b>8 dosis por mes.</b></p>
  </li>
  <li data-start="413" data-end="474">
  <p data-start="415" data-end="474">Tome la dosis <b>30-60 minutos antes de la actividad sexual.</b></p>
  </li>
  <li data-start="475" data-end="562">
  <p data-start="477" data-end="562">Los efectos secundarios comunes incluyen <b>náuseas, enrojecimiento y dolor de cabeza.</b></p>
  </li>
  <li data-start="563" data-end="665">
  <p data-start="565" data-end="665">Evite su uso si tiene <b>presión arterial alta no controlada, enfermedades cardíacas o está embarazada.</b></p></li></ul></div>`,
    },
  },

  "113587f0-3bdf-48a2-abb4-728322c3519c": {
    en: {
      title: "This is your first message to the doctor. Please introduce yourself and, if you wish:",
      description: `<div><ul><li>Ask any questions you may have.</li>
<li>Mention any other medical conditions that were not included in the questions.</li>
<li>Include any additional information you believe your doctor should know.</li>
<li>For example: asthma, high blood pressure, among others.</li></ul></div>`,
      placeholder: 'Example: "Hi doctor, other than what I already answered I am otherwise healthy and excited to get started."',
    },
    es: {
      title: "Este es tu primer mensaje para el médico. Por favor, preséntese y si deseas:",
      description: `<div><ul><li>Puedes realizar cualquier pregunta.</li>
<li>Mencionar otros problemas médicos que no hayan sido mencionados en las preguntas.</li>
<li>Incluir cualquier información adicional que creas que tu médico deba conocer.</li>
<li>Ej. Asma, presión alta, entre otros.</li></ul></div>`,
      placeholder: 'Ejemplo: "Hola doctor/a, aparte de lo que ya respondí, estoy saludable y con ganas de comenzar."',
    },
  },

  "5d96fda7-15b5-4286-8fa4-445430cc4202": {
    en: {
      description: `<div>Note: The use of medications for erectile dysfunction in combination with nitrates (often prescribed for chest pain or angina) or other drugs can be fatal. Please make sure to select the option(s) that apply to your situation.</div>`,
    },
  },
};

/**
 * Legacy: Get the hardcoded description for a question ID
 * Now uses the new QUESTION_OVERRIDES structure
 */
export function getQuestionDescription(
  questionId: string,
  language: "en" | "es"
): string | null {
  const override = QUESTION_OVERRIDES[questionId]?.[language];
  return override?.description || null;
}

/**
 * Get the hardcoded title override for a question ID
 */
export function getQuestionTitle(
  questionId: string,
  language: "en" | "es"
): string | null {
  const override = QUESTION_OVERRIDES[questionId]?.[language];
  return override?.title || null;
}

/**
 * Get the hardcoded placeholder override for a question ID
 */
export function getQuestionPlaceholder(
  questionId: string,
  language: "en" | "es"
): string | null {
  const override = QUESTION_OVERRIDES[questionId]?.[language];
  return override?.placeholder || null;
}
